<template>
	<div class="unlock-map-region-popup__content">
		<div class="unlock-map-region-popup__title">
			{{
				translate("ui.map_expansion_cta").replace(
					"{number}",
					formatNumber(
						(body.maxX - body.minX + (body.axis === "x" ? 1 : 0)) *
							(body.maxY -
								body.minY +
								(body.axis === "y" ? 1 : 0)),
					),
				)
			}}
		</div>
		<div class="unlock-map-region-popup__requirement">
			{{ translate("ui.map_expansion_requirements") }}
		</div>
		<div
			v-for="(quest, index) in quests"
			:key="index"
			class="unlock-map-region-popup__progress"
		>
			<RaggedBoxBackground
				slug="unlock-map-region-progress-box-bg"
				:skin="{ stopColor1: '#f2dcb5', stopColor2: '#fff3dd' }"
			/>
			<div class="progress__content">
				<div class="progress__title">
					{{
						translate(quest.text).replace(
							"{number}",
							formatNumber(quest.getTargetValue()),
						)
					}}
				</div>
				<AppProgressBar
					:progress="
						Math.min(
							quest.getCurrentValue() / quest.getTargetValue(),
							1,
						) * 100
					"
				/>
				<div
					v-if="quest.getCurrentValue() >= quest.getTargetValue()"
					class="progress--completed"
				>
					<div class="progress__text">
						{{ translate("ui.completed") }}
					</div>
					<div class="icon-check" />
				</div>
				<div class="progress__text" v-else>
					{{
						`${formatNumber(quest.getCurrentValue())}/${formatNumber(quest.getTargetValue())}`
					}}
				</div>
			</div>
		</div>
		<div class="unlock-map-region-popup__price">
			<div class="price">
				<div class="price__title">{{ translate("ui.price") }}:</div>
				<div
					:class="[
						'price__bg',
						{ 'price__bg--negative': !isPriceSatisfied },
					]"
				>
					<div>
						{{ formatNumber(price?.[ResourceType.COINS] ?? 0) }}
					</div>
					<AppMainIcon iconName="coins" :iconSize="24" />
				</div>
			</div>
		</div>
		<div class="unlock-map-region-popup__button">
			<AppTextButton
				class="offline-popup__btn"
				:type="StatusTypes.SUCCESS"
				:text="translate('button.expand_map')"
				:disabled="isButtonDisabled"
				@click="purchaseMapExpansion"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useMapExpansionStore, {
	type MapExpansionConfig,
} from "@/store/useMapExpansionStore";
import useResourcesStore from "@/store/useResourcesStore";
import { StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import { computed } from "vue";
import AppMainIcon from "../AppMainIcon.vue";
import AppProgressBar from "../AppProgressBar.vue";
import AppTextButton from "../AppTextButton.vue";
import RaggedBoxBackground from "../SVG/RaggedBoxBackground.vue";

const { body } = defineProps<{
	body: MapExpansionConfig;
}>();
const emit = defineEmits(["close"]);

const { setIsUnlockedById } = useMapExpansionStore();

// netreba computed, nemeni sa pri otvorenom okne
const { id, price, quests, isReadyToUnlockByQuests, isReadyToUnlockByRegions } =
	body;

const isPriceSatisfied = computed((): boolean => {
	const storedCoins = useResourcesStore()[ResourceType.COINS] ?? 0;
	return storedCoins >= (price?.[ResourceType.COINS] ?? 0);
});

const isButtonDisabled = computed(
	(): boolean =>
		!isReadyToUnlockByQuests() ||
		!isReadyToUnlockByRegions() ||
		!isPriceSatisfied.value ||
		body.isUnlocked,
);

const purchaseMapExpansion = (): void => {
	if (isButtonDisabled.value) return;

	useResourcesStore().decrease(
		ResourceType.COINS,
		price?.[ResourceType.COINS] ?? 0,
		`map expansion ${id}`,
	);

	setIsUnlockedById(id);
	useEmitter().emit(EmitterEvents.PhaserUnlockRegion, { id: `${id}` });
	emit("close");
};
</script>

<style lang="scss" scoped>
.unlock-map-region-popup {
	&__content {
		position: relative;
		padding: 0.75rem;
		padding-top: 0.5rem;
	}

	&__title {
		color: #372615;
		font-weight: 800;
		@include font-size(16);
		line-height: 1.25;
		text-align: center;
	}

	&__requirement {
		margin-top: 0.875rem;
		color: #372615;
		@include font-size(14);
		text-align: center;
	}

	&__progress {
		position: relative;
		margin-top: 0.5rem;

		.progress {
			&__content {
				padding: 0.5rem 1.375rem;
			}

			&__title {
				margin-bottom: 0.5rem;
				color: #372615;
				font-weight: 800;
				@include font-size(14);
			}

			&__text {
				margin-top: 0.125rem;
				color: #372615;
				font-weight: 800;
				@include font-size(14);
			}

			&--completed {
				@include flex-row-start;
				gap: 0.25rem;
			}
		}

		:deep(.ragged-box-bg) {
			position: absolute;
			z-index: -1;
			filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
			width: 100%;
			height: 100%;
		}

		:deep(.progress-bar) {
			flex-shrink: 0;
		}

		:deep(.icon-check) {
			margin-top: 0.25rem;
			width: 0.75rem;
			height: 0.75rem;
		}
	}

	&__price {
		margin-top: 0.5rem;
		.price {
			@include flex-col-center;

			&__title {
				color: #372615;
				@include font-size(14);
				text-align: center;
			}

			&__bg {
				margin-top: 0.25rem;
				@include flex-row-center;
				gap: 0.25rem;
				color: #fff3dd;
				@include font-size(14);
				border-width: 0.063rem;
				border-style: solid;
				border-image-source: linear-gradient(
					to right,
					transparent 0%,
					rgba(216, 244, 87, 0.8) 25%,
					rgba(216, 244, 87, 0.8) 75%,
					transparent 100%
				);
				border-image-slice: 1;
				background: linear-gradient(
					to right,
					transparent 0%,
					rgba(25, 179, 69, 0.8) 25%,
					rgba(25, 179, 69, 0.8) 75%,
					transparent 100%
				);
				width: 18rem;
				height: 2rem;
				font-weight: 800;
				@include font-size(24);
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

				&--negative {
					border-image-source: linear-gradient(
						to right,
						transparent 0%,
						rgba(211, 158, 82, 0.8) 25%,
						rgba(211, 158, 82, 0.8) 75%,
						transparent 100%
					);
					background: linear-gradient(
						to right,
						transparent 0%,
						rgba(137, 91, 25, 0.8) 25%,
						rgba(137, 91, 25, 0.8) 75%,
						transparent 100%
					);
					color: #ff3d3d;
				}

				& > div {
					padding-bottom: 0.5rem;
					font-weight: inherit;
				}
			}
		}
	}

	&__button {
		margin-top: 0.5rem;
		@include flex-row-center;
	}
}
</style>
